import { ComisionLimit } from "./SalesComissionsSportsProgramConfig.table";

export enum LimitErrorType {
  NONE = "NONE",
  MONTO_ZERO = "MONTO_ZERO",
  PORCENTAJE_ZERO = "PORCENTAJE_ZERO",
  PORCENTAJE_EXCEEDS_100 = "PORCENTAJE_EXCEEDS_100",
  DESDE_MAYOR_HASTA = "DESDE_MAYOR_HASTA",
  PRIMER_LIMITE_NO_EMPIEZA_EN_1 = "PRIMER_LIMITE_NO_EMPIEZA_EN_1",
  LIMITES_NO_CONSECUTIVOS = "LIMITES_NO_CONSECUTIVOS",
  LIMITE_DUPLICADO = "LIMITE_DUPLICADO"
}

export type ValidationResult = [LimitErrorType, string | null];

export function validateLimit(
  limit: ComisionLimit,
  index: number,
  allLimits: ComisionLimit[],
  tipoMonto: "PORCENTAJE" | "MONETARIO"
): ValidationResult {
  if (!limit) {
    return [LimitErrorType.NONE, null];
  }

  // Error 1: Si el monto/porcentaje es 0 según el tipo
  if (tipoMonto === "PORCENTAJE" && limit.montoPorcentajeParticipante === 0) {
    return [
      LimitErrorType.PORCENTAJE_ZERO,
      "El porcentaje de comisión no puede ser 0%"
    ];
  }
  if (tipoMonto === "MONETARIO" && limit.montoPorParticipante === 0) {
    return [LimitErrorType.MONTO_ZERO, "El monto de comisión no puede ser $0"];
  }

  // Error 2: Si el porcentaje excede 100%
  if (tipoMonto === "PORCENTAJE" && limit.montoPorcentajeParticipante > 100) {
    return [
      LimitErrorType.PORCENTAJE_EXCEEDS_100,
      `El porcentaje no puede exceder 100% (actual: ${limit.montoPorcentajeParticipante}%)`
    ];
  }

  // Error 3: Si "Desde" es mayor que "Hasta" (excepto cuando Hasta = -1 que es "sin límite")
  if (
    limit.numeroParticipantesHasta !== -1 &&
    limit.numeroParticipantesDesde > limit.numeroParticipantesHasta
  ) {
    return [
      LimitErrorType.DESDE_MAYOR_HASTA,
      `El valor "Desde" (${limit.numeroParticipantesDesde}) no puede ser mayor que "Hasta" (${limit.numeroParticipantesHasta})`
    ];
  }

  // Error 4: El primer límite debe empezar en 1 (no necesario al ordenar)
  if (index === 0 && limit.numeroParticipantesDesde !== 1) {
    return [
      LimitErrorType.PRIMER_LIMITE_NO_EMPIEZA_EN_1,
      `El primer límite debe empezar en 1`
    ];
  }

  // Error 5: Los límites "chocan"
  if (index > 0 && index <= allLimits.length) {
    const prevLimit = allLimits[index - 1];

    if (
      prevLimit &&
      prevLimit.numeroParticipantesHasta !== undefined &&
      prevLimit.numeroParticipantesHasta !== -1
    ) {
      const expectedDesde = prevLimit.numeroParticipantesHasta + 1;
      if (limit.numeroParticipantesDesde !== expectedDesde) {
        return [
          LimitErrorType.LIMITES_NO_CONSECUTIVOS,
          `El valor "Desde" debería ser ${expectedDesde} (consecutivo al límite anterior que termina en ${prevLimit.numeroParticipantesHasta})`
        ];
      }
    }
  }

  return [LimitErrorType.NONE, null];
}

export function validateDuplicateLimit(
  limiteHasta: number,
  allLimits: ComisionLimit[],
  editingIndex?: number | null
): ValidationResult {
  const isDuplicate = allLimits.some((row, index) => {
    // Ignorar el que estamos editando
    if (
      editingIndex !== null &&
      editingIndex !== undefined &&
      index === editingIndex
    ) {
      return false;
    }

    return row.numeroParticipantesHasta === limiteHasta;
  });

  if (isDuplicate) {
    return [
      LimitErrorType.LIMITE_DUPLICADO,
      "Ya existe un límite con ese número de usuarios"
    ];
  }

  return [LimitErrorType.NONE, null];
}

export function hasError(
  limit: ComisionLimit,
  index: number,
  allLimits: ComisionLimit[],
  tipoMonto: "PORCENTAJE" | "MONETARIO"
): boolean {
  const [errorType] = validateLimit(limit, index, allLimits, tipoMonto);
  return errorType !== LimitErrorType.NONE;
}

export function hasAnyError(
  limits: ComisionLimit[],
  tipoMonto: "PORCENTAJE" | "MONETARIO"
): boolean {
  return limits.some((limit, index) =>
    hasError(limit, index, limits, tipoMonto)
  );
}

export function getErrorMessage(
  limit: ComisionLimit,
  index: number,
  allLimits: ComisionLimit[],
  tipoMonto: "PORCENTAJE" | "MONETARIO"
): string | null {
  const [_, message] = validateLimit(limit, index, allLimits, tipoMonto);
  return message;
}
